import dayjs from 'dayjs';
import type { AxiosError } from 'axios';
import toast from 'react-hot-toast';
import {
  OneOnOneMeetingStatus,
  ProbationDecisionOutcome,
  ReviewCycleStatus,
  ReviewQuestionType,
  ReviewSubmissionStatus,
  EvaluationReviewer,
  ReviewerType,
  type OneOnOneMeetingSummary,
  type ReviewAnswer,
  type ReviewCycleDetail,
  type ReviewParticipant,
  type ReviewCyclePayload,
  type ReviewForm,
  type ReviewFormSection,
  type ReviewQuestion,
} from '@gokrew/models/performance';
import { isExtensionChoice } from '@gokrew/ui';
import { formatScore as formatLocalizedScore } from '@gokrew/ui/utility/localizedNumbers';
import type {
  ActiveCycleEditableKey,
  DownloadedFile,
  ReviewCycleEditorValues,
  ReviewerTypeSetting,
} from '@/types/performance';
import { withReviewer } from './evaluationReviewer';
import { teamSettingsOf } from './teamFeedback';

export const REVIEWER_TYPES: ReviewerType[] = [
  ReviewerType.SELF,
  ReviewerType.MANAGER,
  ReviewerType.PEER,
];

export const REVIEWER_TYPE_SETTINGS: ReviewerTypeSetting[] = [
  {
    type: ReviewerType.SELF,
    enabledKey: 'selfReviewEnabled',
    dueDateKey: 'selfDueDate',
  },
  {
    type: ReviewerType.MANAGER,
    enabledKey: 'managerReviewEnabled',
    dueDateKey: 'managerDueDate',
  },
  {
    type: ReviewerType.PEER,
    enabledKey: 'peerReviewEnabled',
    dueDateKey: 'peerDueDate',
  },
];

/** Keys the backend still accepts once a cycle has launched. */
export const ACTIVE_EDITABLE_KEYS: ActiveCycleEditableKey[] = [
  'title',
  'description',
  'periodStart',
  'periodEnd',
  'selfDueDate',
  'peerDueDate',
  'managerDueDate',
  'upwardDueDate',
  'autoLockDate',
  'peersPerReviewee',
  'linkedSelfCycleId',
];

export const DEFAULT_SCALE_MAX = 5;
export const MIN_SCALE_MAX = 3;
export const MAX_SCALE_MAX = 10;
export const MAX_PEERS_PER_REVIEWEE = 10;
export const MAX_PEERS = 20;
export const DEFAULT_MEETING_MINUTES = 30;

// ─── Errors ────────────────────────────────────────────────────────────────

/** A company-portal caller with no employee record here (PF-2). */
export const NO_EMPLOYEE_RECORD = 'performance.common.noEmployeeRecord';

/** Whether a failed request carries this performance `errorCode`. */
export const hasPerformanceError = (error: unknown, code: string) =>
  (error as AxiosError<{ errorCode?: string }> | undefined)?.response?.data
    ?.errorCode === code;

/**
 * The axios interceptor already toasts the backend's own (translated) message
 * for every failure except a 403, which it leaves silent. Call this from a
 * mutation's `onError` to cover that one gap without doubling the others.
 */
export const toastIfForbidden = (error: unknown, message: string) => {
  const response = (
    error as AxiosError<{ message?: unknown; errorCode?: string }> | undefined
  )?.response;
  if (response?.status === 403) {
    // A performance 403 with its own code carries its own sentence (e.g.
    // "You can only set up a 1:1 with someone in your reporting team").
    const own =
      response.data?.errorCode && typeof response.data.message === 'string'
        ? response.data.message
        : null;
    toast.error(own ?? message);
  }
};

// ─── Ids ───────────────────────────────────────────────────────────────────

/** Short client-side id for sections, questions and choices (max 64). */
export const createFormId = () =>
  crypto.randomUUID().replace(/-/g, '').slice(0, 12);

// ─── Dates ─────────────────────────────────────────────────────────────────

/** `YYYY-MM-DD` (a `date` column) to a local `Date` for the date picker. */
export const fromIsoDate = (value: string | null | undefined) =>
  value ? dayjs(value).toDate() : undefined;

/** Local `Date` from the date picker to `YYYY-MM-DD`. */
export const toIsoDate = (value: Date | undefined) =>
  value ? dayjs(value).format('YYYY-MM-DD') : null;

/** Today on the viewer's calendar, `YYYY-MM-DD`. */
export const localTodayIso = () => dayjs().format('YYYY-MM-DD');

/**
 * A self or manager review still owed after its due date (A31, the
 * participants tab's `?filter=overdue`). Same rule as the server's
 * `review_cycle_overdue` line.
 */
export const isReviewOverdue = (
  participant: ReviewParticipant,
  cycle: Pick<ReviewCycleDetail, 'selfDueDate' | 'managerDueDate'>,
  today: string
) =>
  !participant.sharedAt &&
  participant.submissions.some((s) => {
    if (s.status === ReviewSubmissionStatus.SUBMITTED) return false;
    const due =
      s.reviewerType === ReviewerType.SELF
        ? cycle.selfDueDate
        : s.reviewerType === ReviewerType.MANAGER
          ? cycle.managerDueDate
          : null;
    return !!due && due.slice(0, 10) < today;
  });

/** `datetime-local` input value to an ISO instant. */
export const localDateTimeToIso = (value: string) =>
  value ? dayjs(value).toISOString() : '';

/** ISO instant to a `datetime-local` input value. */
export const isoToLocalDateTime = (value: string | Date) =>
  dayjs(value).format('YYYY-MM-DDTHH:mm');

/** Next weekday at 10:00, the default time for a new meeting. */
export const nextMeetingDefault = () => {
  let next = dayjs().add(1, 'day').hour(10).minute(0).second(0);
  while (next.day() === 0 || next.day() === 6) next = next.add(1, 'day');
  return next.format('YYYY-MM-DDTHH:mm');
};

// ─── Files ─────────────────────────────────────────────────────────────────

export const downloadFile = ({ blob, filename }: DownloadedFile) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

// ─── Review forms ──────────────────────────────────────────────────────────

/** The form exactly as one reviewer type sees it: its questions, its wording. */
export const formForReviewer = (
  form: ReviewForm,
  type: ReviewerType
): ReviewForm => ({
  sections: form.sections
    .map((section) => ({
      ...section,
      questions: section.questions
        .filter((q) => q.reviewerTypes.includes(type))
        .map((q) => ({ ...q, title: q.titles?.[type]?.trim() || q.title })),
    }))
    .filter((section) => section.questions.length > 0),
});

/** The reviewer types a form has questions for, in the usual order. */
export const formReviewerTypes = (form: ReviewForm): ReviewerType[] =>
  REVIEWER_TYPES.filter((type) =>
    form.sections.some((s) =>
      s.questions.some((q) => q.reviewerTypes.includes(type))
    )
  );

/** The form's probation decision question (V2-5), the first if several. */
export const probationDecisionQuestionId = (form: ReviewForm) =>
  form.sections
    .flatMap((s) => s.questions)
    .find(
      (q) =>
        q.type === ReviewQuestionType.MULTIPLE_CHOICE && q.probationDecision
    )?.id ?? null;

export const countQuestions = (form: ReviewForm) =>
  form.sections.reduce((n, s) => n + s.questions.length, 0);

const isAnswered = (question: ReviewQuestion, answer?: ReviewAnswer) => {
  if (!answer) return false;
  if (question.type === ReviewQuestionType.RATING) return answer.rating != null;
  if (question.type === ReviewQuestionType.MULTIPLE_CHOICE) {
    return !!answer.optionId;
  }
  return !!answer.text?.trim();
};

/**
 * Required questions left blank, and an "extend" probation decision without
 * its date (V2-5), so they can be flagged before submitting.
 */
export const findUnansweredRequired = (
  form: ReviewForm,
  answers: ReviewAnswer[]
) => {
  const byId = new Map(answers.map((a) => [a.questionId, a]));
  return form.sections
    .flatMap((s) => s.questions)
    .filter((q) => {
      const answer = byId.get(q.id);
      if (isExtensionChoice(q, answer) && !answer?.date) return true;
      return q.required && !isAnswered(q, answer);
    })
    .map((q) => q.id);
};

export const createQuestion = (
  type: ReviewQuestionType,
  reviewerTypes: ReviewerType[]
): ReviewQuestion => ({
  id: createFormId(),
  type,
  title: '',
  required: true,
  reviewerTypes,
  visibleToReviewee: true,
  ...(type === ReviewQuestionType.RATING && {
    scaleMax: DEFAULT_SCALE_MAX,
  }),
  ...(type === ReviewQuestionType.MULTIPLE_CHOICE && {
    options: [
      { id: createFormId(), label: '' },
      { id: createFormId(), label: '' },
    ],
  }),
});

export const createSection = (): ReviewFormSection => ({
  id: createFormId(),
  title: '',
  questions: [],
});

/** Moves `list[index]` one step up (-1) or down (1). */
export const moveItem = <T>(list: T[], index: number, step: -1 | 1): T[] => {
  const target = index + step;
  if (target < 0 || target >= list.length) return list;
  const next = [...list];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
};

/**
 * A rating's point names (V2-3) sized to its scale: existing names keep their
 * place, new points start blank, points past the new top are dropped.
 */
export const resizeScaleLabels = (labels: string[], scaleMax: number) =>
  Array.from({ length: scaleMax }, (_, i) => labels[i] ?? '');

/**
 * Turns "Name each point" on or off. On: the end labels seed the first and
 * last point names. Off: the first and last names become the end labels.
 */
export const toggleScaleLabels = (
  question: ReviewQuestion,
  on: boolean
): Partial<ReviewQuestion> => {
  const max = question.scaleMax ?? DEFAULT_SCALE_MAX;
  if (on) {
    const labels = resizeScaleLabels([], max);
    labels[0] = question.scaleMinLabel ?? '';
    labels[max - 1] = question.scaleMaxLabel ?? '';
    return {
      scaleLabels: labels,
      scaleMinLabel: undefined,
      scaleMaxLabel: undefined,
    };
  }
  const labels = question.scaleLabels ?? [];
  return {
    scaleLabels: undefined,
    scaleMinLabel: labels[0]?.trim() || undefined,
    scaleMaxLabel: labels[labels.length - 1]?.trim() || undefined,
  };
};

/** Point names to keep: one per point, none blank (the backend's rule). */
const validScaleLabels = (q: ReviewQuestion) => {
  const labels = (q.scaleLabels ?? []).map((l) => l.trim());
  return labels.length === (q.scaleMax ?? DEFAULT_SCALE_MAX) &&
    labels.every((l) => l.length > 0)
    ? labels
    : null;
};

/**
 * A rating's scale as stored. With every point named (V2-3) the end labels
 * are the first and last names, so the slider's ends always match them.
 */
const cleanScale = (q: ReviewQuestion): Partial<ReviewQuestion> => {
  const labels = validScaleLabels(q);
  const minLabel = labels ? labels[0] : q.scaleMinLabel?.trim();
  const maxLabel = labels ? labels[labels.length - 1] : q.scaleMaxLabel?.trim();
  return {
    scaleMax: q.scaleMax ?? DEFAULT_SCALE_MAX,
    ...(minLabel && { scaleMinLabel: minLabel }),
    ...(maxLabel && { scaleMaxLabel: maxLabel }),
    ...(labels && { scaleLabels: labels }),
    ...(q.commentEnabled && { commentEnabled: true }),
  };
};

/** Drops empty optional strings so the backend's validators accept them. */
const cleanQuestion = (q: ReviewQuestion): ReviewQuestion => {
  const titles = Object.fromEntries(
    Object.entries(q.titles ?? {}).filter(([, v]) => v?.trim())
  );
  return {
    id: q.id,
    type: q.type,
    title: q.title.trim(),
    ...(Object.keys(titles).length > 0 && { titles }),
    ...(q.description?.trim() && { description: q.description.trim() }),
    required: q.required,
    reviewerTypes: q.reviewerTypes,
    visibleToReviewee: q.visibleToReviewee,
    ...(q.type === ReviewQuestionType.RATING && cleanScale(q)),
    ...(q.type === ReviewQuestionType.MULTIPLE_CHOICE && {
      options: (q.options ?? []).map((o) => ({
        id: o.id,
        label: o.label.trim(),
        ...(q.probationDecision && {
          probationOutcome: o.probationOutcome ?? ProbationDecisionOutcome.NONE,
        }),
      })),
      ...(q.probationDecision && { probationDecision: true }),
    }),
  };
};

export const cleanForm = (form: ReviewForm): ReviewForm => ({
  sections: form.sections.map((s) => ({
    id: s.id,
    title: s.title.trim(),
    ...(s.description?.trim() && { description: s.description.trim() }),
    questions: s.questions.map(cleanQuestion),
  })),
});

/** Where the form still needs work before it can be saved. */
export const findFormProblems = (form: ReviewForm) => {
  const problems = new Set<
    | 'sectionTitle'
    | 'questionTitle'
    | 'options'
    | 'scaleLabels'
    | 'probationDecision'
  >();
  let decisions = 0;
  for (const section of form.sections) {
    if (!section.title.trim()) problems.add('sectionTitle');
    for (const q of section.questions) {
      if (!q.title.trim()) problems.add('questionTitle');
      if (
        q.type === ReviewQuestionType.RATING &&
        q.scaleLabels &&
        !validScaleLabels(q)
      ) {
        problems.add('scaleLabels');
      }
      if (
        q.type === ReviewQuestionType.MULTIPLE_CHOICE &&
        q.probationDecision
      ) {
        decisions += 1;
        if (decisions > 1) problems.add('probationDecision');
      }
      if (
        q.type === ReviewQuestionType.MULTIPLE_CHOICE &&
        ((q.options ?? []).length < 2 ||
          (q.options ?? []).some((o) => !o.label.trim()))
      ) {
        problems.add('options');
      }
    }
  }
  return [...problems];
};

// ─── Cycles ────────────────────────────────────────────────────────────────

export const emptyCycleValues = (): ReviewCycleEditorValues => ({
  title: '',
  description: null,
  periodStart: null,
  periodEnd: null,
  ...withReviewer(EvaluationReviewer.MANAGER),
  linkedSelfCycleId: null,
  selfDueDate: null,
  peerDueDate: null,
  managerDueDate: null,
  autoLockDate: null,
  peerAnonymous: true,
  sharePeerFeedback: true,
  revieweeNominatesPeers: false,
  peersPerReviewee: 3,
  requireAcknowledgement: true,
  ...teamSettingsOf(null),
  form: { sections: [] },
});

export const cycleToValues = (
  cycle: ReviewCycleDetail
): ReviewCycleEditorValues => ({
  title: cycle.title,
  description: cycle.description,
  periodStart: cycle.periodStart,
  periodEnd: cycle.periodEnd,
  reviewer: cycle.reviewer,
  linkedSelfCycleId: cycle.linkedSelfCycleId,
  selfReviewEnabled: cycle.selfReviewEnabled,
  managerReviewEnabled: cycle.managerReviewEnabled,
  peerReviewEnabled: cycle.peerReviewEnabled,
  selfDueDate: cycle.selfDueDate,
  peerDueDate: cycle.peerDueDate,
  managerDueDate: cycle.managerDueDate,
  autoLockDate: cycle.autoLockDate,
  peerAnonymous: cycle.peerAnonymous,
  sharePeerFeedback: cycle.sharePeerFeedback,
  revieweeNominatesPeers: cycle.revieweeNominatesPeers,
  peersPerReviewee: cycle.peersPerReviewee,
  requireAcknowledgement: cycle.requireAcknowledgement,
  ...teamSettingsOf(cycle),
  form: cycle.form,
});

/** The body to send: everything on a draft, only what may change once live. */
export const valuesToPayload = (
  values: ReviewCycleEditorValues,
  status: ReviewCycleStatus
): ReviewCyclePayload => {
  const description = values.description?.trim() || null;
  if (status === ReviewCycleStatus.ACTIVE) {
    const payload: ReviewCyclePayload = {};
    for (const key of ACTIVE_EDITABLE_KEYS) {
      Object.assign(payload, { [key]: values[key] });
    }
    return { ...payload, title: values.title.trim(), description };
  }
  return {
    ...values,
    title: values.title.trim(),
    description,
    form: cleanForm(values.form),
  };
};

export const enabledReviewerTypes = (
  settings: Pick<
    ReviewCycleEditorValues,
    'selfReviewEnabled' | 'managerReviewEnabled' | 'peerReviewEnabled'
  >
) =>
  REVIEWER_TYPE_SETTINGS.filter((s) => settings[s.enabledKey]).map(
    (s) => s.type
  );

export const percent = (part: number, total: number) =>
  total > 0 ? Math.round((part / total) * 100) : 0;

/** One decimal in the reader's language ("2,2" in French), or null. */
export const formatScore = (score: number | null | undefined) =>
  score == null ? null : formatLocalizedScore(score);

// ─── 1:1s ──────────────────────────────────────────────────────────────────

/** The meeting to open by default: the next upcoming one, else the latest. */
export const pickDefaultMeetingId = (meetings: OneOnOneMeetingSummary[]) => {
  const upcoming = meetings
    .filter((m) => m.status === OneOnOneMeetingStatus.UPCOMING)
    .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt));
  return upcoming[0]?.id ?? meetings[0]?.id ?? null;
};
